'use client'

import { Check, X } from 'lucide-react'
import { useTranslations } from 'next-intl'

/**
 * Simple Feature Comparison Table
 * Quick overview for people who scroll past the cards
 */
export function FeatureComparisonTable() {
  const t = useTranslations('pricing-v3')

  const features = [
    {
      key: 'custom_domain',
      label: 'Custom domain',
      free: false,
      basic: true,
      premium: true,
      platinum: true,
    },
    {
      key: 'remove_watermark',
      label: 'Remove watermark',
      free: false,
      basic: true,
      premium: true,
      platinum: true,
    },
    {
      key: 'private_projects',
      label: 'Private projects',
      free: false,
      basic: true,
      premium: true,
      platinum: true,
    },
    {
      key: 'payments_storefront',
      label: 'Payments / Storefront',
      free: false,
      basic: false,
      premium: true,
      platinum: true,
    },
    {
      key: 'ai_seo',
      label: 'AI SEO',
      free: false,
      basic: false,
      premium: true,
      platinum: true,
    },
    {
      key: 'conversion_tracking',
      label: 'Conversion tracking',
      free: false,
      basic: false,
      premium: true,
      platinum: true,
    },
    {
      key: 'team_roles',
      label: 'Team roles',
      free: false,
      basic: false,
      premium: true,
      platinum: true,
    },
    {
      key: 'automation',
      label: 'Automation',
      free: false,
      basic: false,
      premium: false,
      platinum: true,
    },
    {
      key: 'priority_support',
      label: 'Priority support',
      free: false,
      basic: false,
      premium: false,
      platinum: true,
    },
  ]

  return (
    <section className="container mx-auto px-4 py-16">
      {/* Section Header */}
      <div className="text-center mb-12">
        <h2 className="text-3xl md:text-4xl font-bold text-white mb-3">
          {t('comparison.title') || 'Simple comparison'}
        </h2>
        <p className="text-base text-white/60">
          {t('comparison.subtitle') || 'For people who scroll'}
        </p>
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="w-full max-w-4xl mx-auto bg-white/5 rounded-xl border border-white/10">
          <thead>
            <tr className="border-b border-white/10">
              <th className="text-left py-4 px-6 text-white/80 font-medium">
                Feature
              </th>
              <th className="text-center py-4 px-4 text-white font-semibold">
                Free
              </th>
              <th className="text-center py-4 px-4 text-white font-semibold">
                Basic
              </th>
              <th className="text-center py-4 px-4 text-white font-semibold bg-purple-500/10">
                <div className="flex flex-col items-center gap-1">
                  <span>Premium</span>
                  <span className="text-xs text-purple-400 font-normal">
                    Most Popular
                  </span>
                </div>
              </th>
              <th className="text-center py-4 px-4 text-white font-semibold">
                Platinum
              </th>
            </tr>
          </thead>
          <tbody>
            {features.map((feature, idx) => (
              <tr
                key={feature.key}
                className={`border-b border-white/5 hover:bg-white/5 transition-colors ${
                  idx === features.length - 1 ? 'border-b-0' : ''
                }`}
              >
                <td className="py-3 px-6 text-white/80 text-sm">
                  {feature.label}
                </td>
                <td className="text-center py-3 px-4">
                  {feature.free ? (
                    <Check className="w-5 h-5 text-green-400 mx-auto" />
                  ) : (
                    <X className="w-5 h-5 text-white/20 mx-auto" />
                  )}
                </td>
                <td className="text-center py-3 px-4">
                  {feature.basic ? (
                    <Check className="w-5 h-5 text-green-400 mx-auto" />
                  ) : (
                    <X className="w-5 h-5 text-white/20 mx-auto" />
                  )}
                </td>
                <td className="text-center py-3 px-4 bg-purple-500/10">
                  {feature.premium ? (
                    <Check className="w-5 h-5 text-green-400 mx-auto" />
                  ) : (
                    <X className="w-5 h-5 text-white/20 mx-auto" />
                  )}
                </td>
                <td className="text-center py-3 px-4">
                  {feature.platinum ? (
                    <Check className="w-5 h-5 text-green-400 mx-auto" />
                  ) : (
                    <X className="w-5 h-5 text-white/20 mx-auto" />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Mobile View */}
      <div className="md:hidden space-y-4">
        {features.map((feature) => (
          <div
            key={feature.key}
            className="bg-white/5 rounded-lg p-4 border border-white/10"
          >
            <div className="text-white font-medium mb-3">{feature.label}</div>
            <div className="grid grid-cols-4 gap-2">
              <div className="text-center">
                <div className="text-xs text-white/60 mb-2">Free</div>
                {feature.free ? (
                  <Check className="w-4 h-4 text-green-400 mx-auto" />
                ) : (
                  <X className="w-4 h-4 text-white/20 mx-auto" />
                )}
              </div>
              <div className="text-center">
                <div className="text-xs text-white/60 mb-2">Basic</div>
                {feature.basic ? (
                  <Check className="w-4 h-4 text-green-400 mx-auto" />
                ) : (
                  <X className="w-4 h-4 text-white/20 mx-auto" />
                )}
              </div>
              <div className="text-center bg-purple-500/10 rounded p-1">
                <div className="text-xs text-purple-400 mb-2">Premium</div>
                {feature.premium ? (
                  <Check className="w-4 h-4 text-green-400 mx-auto" />
                ) : (
                  <X className="w-4 h-4 text-white/20 mx-auto" />
                )}
              </div>
              <div className="text-center">
                <div className="text-xs text-white/60 mb-2">Platinum</div>
                {feature.platinum ? (
                  <Check className="w-4 h-4 text-green-400 mx-auto" />
                ) : (
                  <X className="w-4 h-4 text-white/20 mx-auto" />
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  )
}
